/**
 * `LocalRelay` — the in-process relay used for single-player and headless runs.
 *
 * It commits immediately, but through exactly the same scheduler, validation,
 * ordering, and ring as the network relay. That is the point of KTD3: every
 * single-player test exercises the multiplayer path, so network play is
 * validated continuously rather than at the end.
 */

import type { Command, Rejection } from './commands.js'
import { type Relay, Scheduler, type SchedulerOptions } from './relay.js'

export interface LocalRelayOptions extends SchedulerOptions {
  /** Slots the local process simulates — all of them for a skirmish. */
  slots?: number[]
  onReject?: (cmd: Command, rejection: Rejection) => void
}

export class LocalRelay implements Relay {
  private readonly scheduler: Scheduler
  private readonly onReject: ((cmd: Command, r: Rejection) => void) | undefined

  constructor(opts: LocalRelayOptions) {
    this.scheduler = new Scheduler(opts)
    this.onReject = opts.onReject
  }

  get localSlot(): number {
    return this.scheduler.localSlot
  }

  get delay(): number {
    return this.scheduler.delay
  }

  /** Exposed for tests and the AI, which submits on behalf of other slots. */
  get targetTick(): number {
    return this.scheduler.targetTick
  }

  submit(input: Omit<Command, 'slot' | 'seq'> & { slot?: number; seq?: number }): Rejection | null {
    const slot = input.slot ?? this.scheduler.localSlot
    const cmd = { ...input, slot, seq: this.scheduler.nextSeq() } as Command
    const execTick = this.scheduler.targetTick

    // In-process, the sender is whoever claims to be sending — but the check
    // still runs, because the local path must reject exactly what the network
    // path rejects or the two produce different states from the same input.
    const rejection = this.scheduler.check(cmd, execTick, slot)
    if (rejection) {
      this.onReject?.(cmd, rejection)
      return rejection
    }

    this.scheduler.ring.stage(execTick, cmd)
    return null
  }

  /**
   * Commit on demand. Ticks inside the delay window at match start have no
   * submissions by construction and commit empty — which is correct, and is
   * why the first orders a player can issue land at tick `delay + 1`.
   */
  commandsFor(tick: number): readonly Command[] | null {
    if (tick > this.scheduler.currentTick + 1 + this.scheduler.delay) return null
    return this.scheduler.ring.commit(tick)
  }

  onTickExecuted(tick: number): void {
    this.scheduler.currentTick = tick
    // Release the slot two rings behind, so a late read of a recent tick is
    // still possible while the ring never grows.
    this.scheduler.ring.release(tick - 1)
  }

  dispose(): void {
    this.scheduler.ring.clear()
  }
}
